import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { AccountRecord, useCheckMetaSubscriptionForAccountQuery } from '../../api/client'
import { Link } from 'react-router-dom'

type Props = {
    account: AccountRecord
    // When true, render a single tick/cross icon with a short "sub" label and a
    // tooltip — for at-a-glance status in the account list. Full labelled detail
    // (default) is shown in the expandable admin row.
    compact?: boolean
}

function MetaAccountSubscriptionStatus(props: Props) {
    const { account, compact } = props

    const { data, loading, error } = useCheckMetaSubscriptionForAccountQuery({
        variables: { accountId: account.id }
    })

    if (compact) {
        if (loading) return <span className="text-[11px] text-ink-400">sub…</span>
        const ok = !error && data?.checkMetaSubscriptionForAccount
        return (
            <span
                className="inline-flex items-center gap-1 text-[11px]"
                title={
                    error
                        ? 'Error checking subscription status'
                        : ok
                          ? 'Has subscription'
                          : 'Subscription missing'
                }
            >
                {ok ? (
                    <CheckCircleIcon className="h-4 w-4 text-green-500" aria-hidden="true" />
                ) : (
                    <XCircleIcon className="h-4 w-4 text-red-500" aria-hidden="true" />
                )}
                <span className={ok ? 'text-ink-400' : 'text-red-600'}>sub</span>
            </span>
        )
    }

    if (loading) return <div>Checking subscription status...</div>
    if (error)
        return (
            <div className="text-red-500">
                Error checking subscription status.{' '}
                <Link
                    className={`text-arwen-600 p-2 underline `}
                    to={`/account/${account.id}/settings/validation`}
                >
                    Click here to try again
                </Link>
            </div>
        )

    const isSubscribed = data?.checkMetaSubscriptionForAccount

    return (
        <>
            <div className="flex items-center space-x-2 justify-center">
                {isSubscribed ? (
                    <CheckCircleIcon className="h-6 w-6 text-green-500" aria-hidden="true" />
                ) : (
                    <XCircleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />
                )}
                <span className="text-gray-700">
                    {isSubscribed ? (
                        'Has subscription'
                    ) : (
                        <span>
                            Subscription missing!
                            <Link
                                className={`text-arwen-600 p-2 underline `}
                                to={`/account/${account.id}/settings/validation`}
                            >
                                Go here to create one
                            </Link>
                        </span>
                    )}
                </span>
            </div>
        </>
    )
}

export default MetaAccountSubscriptionStatus
